import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../../utils/api-data-source';
import { UserPublicProfileComponent } from '../../../shared/user-public-profile/user-public-profile.component';
import { AssetMaintenanceRecordsService } from '../../asset-maintenance-records/asset-maintenance-records.service';
import { AssetMaintenanceSchedulesService } from '../asset-maintenance-schedules.service';

@Component({
  selector: 'app-asset-maintenance-schedules-selector',
  templateUrl: './asset-maintenance-schedules-selector.component.html',
  styleUrls: ['./asset-maintenance-schedules-selector.component.scss']
})
export class AssetMaintenanceSchedulesSelectorComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'title',
    // 'schedule_mode', // Phase 4
    'maintenance_type',
    'flags',
    'default_priority',
    'next_due_at',
    'next_auto_create_at',
    'users',
    'date_created'
  ];
  dataSource: AssetMaintenanceSchedulesSelectorDataSource;
  returnAllSelected = false;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly MAX_VISIBLE_ASSIGNED_USERS = 3;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetMaintenanceRecordsService: AssetMaintenanceRecordsService,
    public assetMaintenanceSchedulesService: AssetMaintenanceSchedulesService,
    public dialogRef: MatDialogRef<AssetMaintenanceSchedulesSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // set asset id if it is not present to 0
    if (typeof this.data['asset_id'] === 'undefined') {
      this.data['asset_id'] = 0;
    }

    // set multiple if it is not present to false
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // set required if it is not present to false
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // construct the datasource
    this.dataSource = new AssetMaintenanceSchedulesSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // set a default selection of nothing if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // loop and select the values
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    this.returnAllSelected = this.data.returnAllSelected;
  }

  ngOnInit() {
    this.dataSource.asset_id = this.data['asset_id'];

    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clears the search bar and performs a search.
   *
   * @returns {void}
   */
  onClearSearch(): void {
    // clear the search field in the datasource
    this.dataSource.search = '';
    // search for records
    this.dataSource.getData(true);
  }

  /**
   * Clear the current selection.
   *
   * @returns {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    if (this.returnAllSelected) {
      return this.dialogRef.close({
        selected: this.dataSource.selection.selected,
        isAllSelected: this.dataSource.isAllSelected()
      });
    }

    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      if (this.dataSource.selection.selected.length) {
        response = this.dataSource.selection.selected[0];
      } else {
        response = 0;
      }
    }

    this.dialogRef.close(response);
  }

  /**
   * Closes the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Retrieves a tooltip string containing names of remaining users beyond a maximum visible limit.
   *
   * @param {any[]} users - An array of user objects.
   * @returns {string} A string containing the names of users beyond the maximum visible count.
   */
  getRemainingUsersTooltip(users: any[]): string {
    if (!users || users.length <= this.MAX_VISIBLE_ASSIGNED_USERS) {
      return '';
    }

    return users
      .slice(this.MAX_VISIBLE_ASSIGNED_USERS)
      .map((user) => user.contact_person)
      .join(', ');
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   * @return {void}
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
      }
    );
  }
}

export class AssetMaintenanceSchedulesSelectorDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = 'false';
  asset_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/maintenance-schedules`, resetOffset, {
      archived: this.archived
    });
  }
}
